import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { useSeo } from "../lib/seo";
import { useAudio } from "../contexts/AudioContext";
import { Play, Pause, MapPin, CalendarDays, ChevronLeft, ChevronRight, ListMusic } from "lucide-react";
import ShareButtons from "../components/ShareButtons";
import FollowDeityButton from "../components/FollowDeityButton";
import { toAudioItem } from "./Bhajans";

export default function DeityDetail() {
  const { slug } = useParams();
  const [d, setD] = useState(null);
  const [all, setAll] = useState([]);
  const [missing, setMissing] = useState(false);
  const { playName, playing, currentName, stop, playQueue } = useAudio();

  useEffect(() => {
    setD(null); setMissing(false);
    api.get(`/deities/${slug}`).then((r) => setD(r.data)).catch(() => setMissing(true));
    window.scrollTo(0, 0);
  }, [slug]);
  useEffect(() => { api.get("/deities").then((r) => setAll(r.data.items)); }, []);

  useSeo(d?.name, d && {
    "@context": "https://schema.org", "@type": "Article", headline: `${d.name}: ${d.title}`,
    image: d.image_url, description: d.story?.slice(0, 200), about: { "@type": "Thing", name: d.name },
  });

  if (missing) return <div data-testid="deity-not-found" className="p-16 text-center text-[#63534E]">Deity not found. <Link to="/deities" className="text-[#E65100]">See all gods & goddesses</Link></div>;
  if (!d) return <div className="p-10 text-[#63534E]">Loading…</div>;

  const idx = all.findIndex((x) => x.slug === slug);
  const prev = idx > 0 ? all[idx - 1] : null;
  const next = idx >= 0 && idx < all.length - 1 ? all[idx + 1] : null;
  const chanting = playing && currentName?.deity === d.slug;
  const tracks = d.tracks || [];
  const playTrack = (t) => playName(toAudioItem(d, t));
  const chant = () => chanting ? stop() : tracks[0] ? playTrack(tracks[0])
    : playName({ id: null, deity: d.slug, sanskrit: d.mantra, english: d.name, meaning: d.mantra_meaning });

  return (
    <div data-testid="deity-detail" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <Link to="/deities" data-testid="deity-back" className="text-sm text-[#8C6D3B] hover:text-[#4A0E17]">← All gods & goddesses</Link>
      <div className="mt-6 grid lg:grid-cols-5 gap-10">
        <div className="lg:col-span-2">
          <div className="rounded-3xl overflow-hidden sacred-border lg:sticky lg:top-24 bg-[#4A0E17]">
            <img src={d.image_url} alt={d.name} className="w-full aspect-square object-cover" />
          </div>
        </div>
        <div className="lg:col-span-3">
          <div className="overline">{d.category}</div>
          <div className="font-devanagari text-3xl text-[#8C6D3B] mt-3">{d.sanskrit}</div>
          <h1 data-testid="deity-name" className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#4A0E17] leading-tight">{d.name}</h1>
          <div className="text-base md:text-lg text-[#E65100] mt-2">{d.title}</div>
          <div className="mt-5 flex flex-wrap gap-3">
            <FollowDeityButton slug={d.slug} name={d.name} />
            {tracks.length > 1 && <button data-testid="deity-playall-btn" onClick={() => playQueue(tracks.map((t) => toAudioItem(d, t)))} className="btn-ghost-gold text-sm"><ListMusic className="w-4 h-4" /> Play all {tracks.length}</button>}
          </div>

          <div data-testid="deity-mantra" className="mt-8 bg-[#4A0E17] text-[#FAF6F0] rounded-2xl p-7 relative overflow-hidden">
            <div className="absolute -right-4 -bottom-10 font-devanagari text-[160px] text-[#D4AF37] opacity-10 leading-none select-none">ॐ</div>
            <div className="overline text-[#D4AF37]">Mantra</div>
            <div className="font-devanagari text-2xl md:text-3xl mt-3 leading-relaxed text-[#F3E5AB]">{d.mantra}</div>
            <div className="text-sm text-[#FAF6F0]/75 mt-3 italic">{d.mantra_meaning}</div>
            <button data-testid="deity-chant-btn" onClick={chant} className="btn-saffron mt-6 relative">
              {chanting ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />} {chanting ? "Pause" : tracks[0] ? `Play ${tracks[0].kind}` : "Chant now"}
            </button>
          </div>

          {tracks.length > 0 && (
            <div data-testid="deity-tracks" className="mt-6 bg-[#FFFDF9] sacred-border rounded-2xl divide-y divide-[#D4AF37]/25">
              {tracks.map((t) => {
                const on = playing && currentName?.track === t.id;
                return (
                  <button key={t.id} data-testid={`deity-track-${t.id}`} onClick={() => (on ? stop() : playTrack(t))} className="w-full flex items-center gap-4 px-5 py-3 text-left hover:bg-[#F3E5AB]/30 transition-sacred">
                    <span className={`w-9 h-9 rounded-full flex items-center justify-center ${on ? "bg-[#E65100] text-white" : "bg-[#4A0E17] text-[#D4AF37]"}`}>{on ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}</span>
                    <span className="flex-1"><span className="font-semibold text-[#4A0E17]">{t.title}</span><span className="block overline !text-[0.6rem]">{t.kind}</span></span>
                  </button>
                );
              })}
            </div>
          )}

          <h2 className="font-serif text-2xl text-[#4A0E17] mt-10">Story & significance</h2>
          <p data-testid="deity-story" className="text-[#1F1412] mt-3 leading-relaxed text-base md:text-lg">{d.story}</p>

          <dl className="mt-8 grid sm:grid-cols-2 gap-4">
            {[["Vahana (mount)", d.vahana], ["Consort", d.consort]].filter(([, v]) => v && v !== "—").map(([k, v]) => (
              <div key={k} className="bg-[#FFFDF9] sacred-border rounded-xl p-4"><dt className="overline">{k}</dt><dd className="font-serif text-xl text-[#4A0E17] mt-1">{v}</dd></div>
            ))}
          </dl>
          {d.symbols?.length > 0 && (
            <div className="mt-6 flex flex-wrap gap-2">{d.symbols.map((s) => <span key={s} className="text-sm bg-[#F3E5AB]/50 border border-[#D4AF37]/40 rounded-full px-3 py-1 text-[#4A0E17]">{s}</span>)}</div>
          )}

          <DeityTemples temples={d.temples} />
          <DeityFestivals festivals={d.festivals} />
          <div className="mt-10"><ShareButtons text={`${d.name}: ${d.mantra}`} /></div>

          <div className="mt-12 flex justify-between gap-4 border-t border-[#D4AF37]/40 pt-6">
            {prev ? <Link data-testid="deity-prev" to={`/deities/${prev.slug}`} className="flex items-center gap-2 text-[#4A0E17] hover:text-[#E65100]"><ChevronLeft className="w-4 h-4" /> {prev.name}</Link> : <span />}
            {next && <Link data-testid="deity-next" to={`/deities/${next.slug}`} className="flex items-center gap-2 text-[#4A0E17] hover:text-[#E65100] text-right">{next.name} <ChevronRight className="w-4 h-4" /></Link>}
          </div>
        </div>
      </div>
    </div>
  );
}

function DeityTemples({ temples }) {
  if (!temples?.length) return null;
  return (
    <>
      <h2 className="font-serif text-2xl text-[#4A0E17] mt-10">Sacred temples</h2>
      <ul data-testid="deity-temples" className="mt-4 grid sm:grid-cols-2 gap-3">
        {temples.map((t) => <li key={t} className="flex items-start gap-2 text-[#1F1412]"><MapPin className="w-4 h-4 text-[#E65100] mt-1 shrink-0" /> {t}</li>)}
      </ul>
    </>
  );
}

function DeityFestivals({ festivals }) {
  if (!festivals?.length) return null;
  return (
    <>
      <h2 className="font-serif text-2xl text-[#4A0E17] mt-10">Festivals</h2>
      <div data-testid="deity-festivals" className="mt-4 space-y-3">
        {festivals.map((f) => (
          <Link key={f.slug} to={`/festivals#${f.slug}`} className="flex items-center gap-4 bg-[#FFFDF9] sacred-border rounded-xl p-4 gold-glow transition-sacred">
            <CalendarDays className="w-5 h-5 text-[#E65100] shrink-0" />
            <div className="flex-1"><div className="font-semibold text-[#4A0E17]">{f.name}</div><div className="text-xs text-[#63534E]">{f.region}</div></div>
            <div className="text-right font-mono text-xs text-[#8C6D3B]"><div>{f.date}</div><div>in {f.days_away}d</div></div>
          </Link>
        ))}
      </div>
    </>
  );
}
